import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Plus, X, Calendar, Clock, User, List, History, ArrowLeft, Trash2, Edit } from 'lucide-react';
import { format, parse, startOfWeek, endOfWeek, addDays, subDays, addMonths, subMonths, addYears, subYears, isSameDay, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { formatPtDayMonth, formatPtMonthYear, formatPtWeekday, formatYear, formatDDMMYYYY } from '../utils/dateI18n';
import { UserRole, UserType } from '../types/index';
import { roundAndSetDuration } from '../utils/timeUtils';
import { isOccurrenceCompleted, shouldShowTaskOnDate } from '../utils/taskRecurrence';

export default function OperationsView({ currentUser, currentCompany, movements, tasks, techJobs, setTasks, team, onBack }) {
  const [viewType, setViewType] = useState('KANBAN'); // 'KANBAN', 'LIST', 'DAY', 'WEEK', 'MONTH', 'YEAR', 'HISTORY'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchFilter, setSearchFilter] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [viewingTask, setViewingTask] = useState(null); // Task being viewed in details modal
  const [editingTask, setEditingTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedOverColumn, setDraggedOverColumn] = useState(null);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  
  // Colunas customizadas (guardadas no localStorage por empresa)
  const [customColumns, setCustomColumns] = useState([]);
  
  // Carregar colunas customizadas quando a empresa mudar
  useEffect(() => {
    if (currentCompany?.id) {
      try {
        const saved = localStorage.getItem(`kanban-columns-${currentCompany.id}`);
        if (saved) {
          setCustomColumns(JSON.parse(saved));
        } else {
          setCustomColumns([]);
        }
      } catch {
        setCustomColumns([]);
      }
    }
  }, [currentCompany?.id]);
  
  // Garantir que team é sempre um array
  const safeTeam = team || [];
  const assignableTeam = safeTeam.filter(u => u?.role !== UserRole.ADMIN);
  
  // Form state
  const [taskForm, setTaskForm] = useState({
    name: '',
    notes: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    assignedTo: currentUser?.id || '',
    status: 'A Fazer',
    autoSchedule: false,
    repeatType: 'DAILY', // DAILY | EVERY_OTHER_DAY | WEEKLY | MONTHLY | CUSTOM_DAYS
    repeatIntervalDays: 1
  });

  // Reiniciar formulário
  const resetForm = () => {
    // Usar a hora atual arredondada para a meia hora mais próxima
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    const defaultTime = roundAndSetDuration(currentTime);
    
    setTaskForm({
      name: '',
      notes: '',
      date: format(now, 'yyyy-MM-dd'),
      startTime: defaultTime.startTime,
      endTime: defaultTime.endTime,
      assignedTo: currentUser?.id || '',
      status: 'A Fazer',
      autoSchedule: false,
      repeatType: 'DAILY',
      repeatIntervalDays: 1
    });
    setEditingTask(null);
  };

  // Para Kanban e Lista: tarefas de hoje ou que ficaram por fazer ontem (arrasto para amanhã)
  const tasksForKanbanAndList = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    let list = tasks.filter(task => {
      const hasDate = task.date != null && String(task.date).trim() !== '';
      const completed = task.isCompleted || task.status === 'Concluído';
      const completedStr = typeof task.completedDate === 'string' ? String(task.completedDate).slice(0, 10) : null;

      if (!hasDate) return true;
      if (shouldShowTaskOnDate(task, todayStr)) return true;
      if (completed && completedStr === todayStr) return true;
      return false;
    });
    if (searchFilter) {
      const f = searchFilter.toLowerCase();
      list = list.filter(task =>
        task.name?.toLowerCase().includes(f) ||
        task.notes?.toLowerCase().includes(f) ||
        task.status?.toLowerCase().includes(f)
      );
    }
    return list;
  }, [tasks, searchFilter]);

  // Filtrar tarefas baseado na busca e na data
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    // Filtro de busca
    if (searchFilter) {
      const filter = searchFilter.toLowerCase();
      filtered = filtered.filter(task => 
        task.name?.toLowerCase().includes(filter) ||
        task.notes?.toLowerCase().includes(filter) ||
        task.status?.toLowerCase().includes(filter)
      );
    }

    // Filtro por data baseado na visualização
    if (viewType === 'HISTORY') {
      // History: apenas tarefas concluídas de dias anteriores (não do dia atual)
      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');
      filtered = filtered.filter(task => {
        if (!task.isCompleted && task.status !== 'Concluído') return false;
        
        // Priorizar completedDate se existir
        if (task.completedDate) {
          return task.completedDate !== todayStr;
        }
        
        // Fallback para data original da tarefa
        if (!task.date) return false;
        try {
          const taskDate = parse(task.date, 'yyyy-MM-dd', new Date());
          // Incluir apenas tarefas concluídas que NÃO são do dia atual
          return !isSameDay(taskDate, today);
        } catch {
          return false;
        }
      });
    } else {
      // Para todas as outras visualizações:
      // - Tarefas concluídas: aparecem só no dia de cada uma (data da tarefa ou data de conclusão)
      // - Tarefas não concluídas: conforme viewType
      const today = new Date();
      filtered = filtered.filter(task => {
        const isCompleted = task.isCompleted || task.status === 'Concluído';
        
        if (isCompleted) {
          // Tarefas concluídas: mostrar apenas no dia que lhes pertence (task.date ou completedDate)
          const taskDateStr = typeof task.date === 'string' ? task.date : (task.date?.toDate ? format(task.date.toDate(), 'yyyy-MM-dd') : null);
          const completedDateStr = task.completedDate || null;
          if (viewType === 'DAY') {
            const viewDateStr = format(currentDate, 'yyyy-MM-dd');
            return (taskDateStr === viewDateStr) || (completedDateStr === viewDateStr);
          }
          if (viewType === 'WEEK') {
            const viewStart = startOfWeek(currentDate, { weekStartsOn: 1 });
            const viewEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
            const inWeek = (str) => {
              if (!str) return false;
              try {
                const d = parse(str, 'yyyy-MM-dd', new Date());
                return d >= viewStart && d <= viewEnd;
              } catch { return false; }
            };
            return inWeek(taskDateStr) || inWeek(completedDateStr);
          }
          if (viewType === 'MONTH') {
            const viewMonth = format(currentDate, 'yyyy-MM');
            const inMonth = (str) => str && str.startsWith(viewMonth);
            return inMonth(taskDateStr) || inMonth(completedDateStr);
          }
          if (viewType === 'YEAR') {
            const viewYear = format(currentDate, 'yyyy');
            const inYear = (str) => str && str.startsWith(viewYear);
            return inYear(taskDateStr) || inYear(completedDateStr);
          }
          // KANBAN e LIST: concluídas só se a data da tarefa ou conclusão é hoje
          const todayStr = format(today, 'yyyy-MM-dd');
          return (taskDateStr === todayStr) || (completedDateStr === todayStr);
        }
        
        // Tarefas não concluídas: data + recorrência + arrasto (por fazer ontem aparece hoje).
        if (viewType === 'DAY') {
          if (!task.date) return true;
          return shouldShowTaskOnDate(task, format(currentDate, 'yyyy-MM-dd'));
        } else if (viewType === 'WEEK') {
          if (!task.date) return true;
          const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
          for (let d = weekStart; d <= weekEnd; d = addDays(d, 1)) {
            if (shouldShowTaskOnDate(task, format(d, 'yyyy-MM-dd'))) return true;
          }
          return false;
        } else if (viewType === 'MONTH') {
          if (!task.date) return true;
          const monthStart = startOfMonth(currentDate);
          const monthEnd = endOfMonth(currentDate);
          for (let d = monthStart; d <= monthEnd; d = addDays(d, 1)) {
            if (shouldShowTaskOnDate(task, format(d, 'yyyy-MM-dd'))) return true;
          }
          return false;
        } else if (viewType === 'YEAR') {
          if (!task.date) return true;
          const y = currentDate.getFullYear();
          const yearStart = new Date(y, 0, 1);
          const yearEnd = new Date(y, 11, 31);
          for (let d = yearStart; d <= yearEnd; d = addDays(d, 1)) {
            if (shouldShowTaskOnDate(task, format(d, 'yyyy-MM-dd'))) return true;
          }
          return false;
        }
        // KANBAN e LIST: tratado num passo separado no final
        return true;
      });
    }

    // Passo explícito: em Kanban e Lista – hoje, concluídas hoje, ou que ficaram por fazer ontem (arrasto)
    if (viewType === 'KANBAN' || viewType === 'LIST') {
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      filtered = filtered.filter(task => {
        if (!task.date) return true;
        if (shouldShowTaskOnDate(task, todayStr)) return true;
        if (isOccurrenceCompleted(task, todayStr)) return true;
        return false;
      });
    }

    return filtered;
  }, [tasks, searchFilter, viewType, currentDate]);

  const parseTaskDate = (task) => {
    if (!task?.date) return null;
    try {
      return parse(task.date, 'yyyy-MM-dd', new Date());
    } catch {
      return null;
    }
  };

  // Função para calcular a data da Páscoa (algoritmo de Gauss)
  const getEasterDate = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  // Função para obter feriados de um ano específico
  const getHolidays = (year) => {
    const holidays = [];
    
    // Feriados fixos
    holidays.push({ date: new Date(year, 0, 1), name: 'Ano Novo' });
    holidays.push({ date: new Date(year, 3, 25), name: 'Dia da Liberdade' });
    holidays.push({ date: new Date(year, 4, 1), name: 'Dia do Trabalhador' });
    holidays.push({ date: new Date(year, 5, 10), name: 'Dia de Portugal' });
    holidays.push({ date: new Date(year, 7, 15), name: 'Assunção de Nossa Senhora' });
    holidays.push({ date: new Date(year, 9, 5), name: 'Implantação da República' });
    holidays.push({ date: new Date(year, 10, 1), name: 'Dia de Todos os Santos' });
    holidays.push({ date: new Date(year, 11, 1), name: 'Restauração da Independência' });
    holidays.push({ date: new Date(year, 11, 8), name: 'Imaculada Conceição' });
    holidays.push({ date: new Date(year, 11, 25), name: 'Natal' });
    
    // Feriados móveis baseados na Páscoa
    const easter = getEasterDate(year);
    const goodFriday = subDays(easter, 2);
    const easterMonday = addDays(easter, 1);
    const corpusChristi = addDays(easter, 60);
    
    holidays.push({ date: goodFriday, name: 'Sexta-feira Santa' });
    holidays.push({ date: easter, name: 'Páscoa' });
    holidays.push({ date: easterMonday, name: 'Segunda-feira de Páscoa' });
    holidays.push({ date: corpusChristi, name: 'Corpo de Deus' });
    
    return holidays;
  };

  // Função para obter feriado de uma data específica
  const getHolidayForDate = (date) => {
    const year = date.getFullYear();
    const holidays = getHolidays(year);
    const dateKey = format(date, 'yyyy-MM-dd');
    
    return holidays.find(h => {
      const holidayKey = format(h.date, 'yyyy-MM-dd');
      return holidayKey === dateKey;
    });
  };

  const sortByTime = (a, b) => {
    const at = (a?.startTime || '99:99');
    const bt = (b?.startTime || '99:99');
    return at.localeCompare(bt);
  };

  // Converter hora HH:mm para minutos do dia (0-1440)
  const timeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calcular posição e altura de uma tarefa na timeline
  const getTaskPosition = (task, startHour = 0) => {
    const startMinutes = timeToMinutes(task.startTime || '00:00');
    const endMinutes = timeToMinutes(task.endTime || task.startTime || '01:00');
    const duration = endMinutes - startMinutes;
    
    // Cada hora = 60px de altura (ajustável)
    const hourHeight = 60;
    const top = (startMinutes / 60) * hourHeight;
    // Mínimo 60px (1 hora) para garantir que o conteúdo seja visível
    const height = Math.max((duration / 60) * hourHeight, 60);
    
    return { top, height };
  };

  const getAssignedName = (task) => {
    if (!task?.assignedTo) return 'Não atribuído';
    return safeTeam.find(u => u.id === task.assignedTo)?.name || 'Não atribuído';
  };

  const TaskCard = ({ task, compact = false, ultraCompact = false }) => {
    const assignedName = getAssignedName(task);
    const nameInitials = assignedName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    
    if (ultraCompact) {
      // Versão ultra compacta para visualização semanal
      return (
        <div
          onClick={() => handleViewTask(task)}
          className="bg-slate-50 border border-slate-200 rounded-lg p-2 hover:shadow-md transition-shadow cursor-pointer"
        >
          {/* Nome da tarefa */}
          <p className="font-semibold text-slate-900 text-xs mb-1.5 truncate leading-tight">{task.name}</p>
          
          {/* Informações compactas */}
          <div className="flex items-center justify-between gap-1.5">
            {task.startTime && (
              <div className="flex items-center gap-1 text-[10px] text-slate-500">
                <Clock size={10} />
                <span className="whitespace-nowrap">{task.startTime}{task.endTime ? `-${task.endTime.split(':')[0]}:${task.endTime.split(':')[1]}` : ''}</span>
              </div>
            )}
            {assignedName !== 'Não atribuído' && (
              <div className="flex items-center gap-1 text-[10px] text-slate-500 truncate max-w-[60px]">
                <User size={10} />
                <span className="truncate">{nameInitials}</span>
              </div>
            )}
          </div>
          
          {/* Status badge compacto */}
          <div className="mt-1.5">
            <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold leading-tight ${
              task.status === 'A Fazer' ? 'bg-blue-100 text-blue-700' :
              task.status === 'Em Progresso' ? 'bg-yellow-100 text-yellow-700' :
              'bg-green-100 text-green-700'
            }`}>
              {task.status === 'A Fazer' ? 'AF' : task.status === 'Em Progresso' ? 'EP' : 'C'}
            </span>
          </div>
        </div>
      );
    }
    
    return (
      <div
        onClick={() => handleViewTask(task)}
        className={`bg-slate-50 border border-slate-200 rounded-lg ${compact ? 'p-3' : 'p-4'} hover:shadow-md transition-shadow cursor-pointer`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className={`font-semibold text-slate-900 ${compact ? 'text-sm' : ''} mb-1 truncate`}>{task.name}</p>
            {!compact && task.notes && (
              <p className="text-sm text-slate-500 mb-2 line-clamp-2">{task.notes}</p>
            )}
            <div className={`flex flex-wrap items-center ${compact ? 'gap-3' : 'gap-4'} text-xs text-slate-400`}>
              {task.startTime && (
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  <span>{task.endTime ? `${task.startTime} - ${task.endTime}` : task.startTime}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <User size={12} />
                <span>{assignedName}</span>
              </div>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
            task.status === 'A Fazer' ? 'bg-blue-100 text-blue-700' :
            task.status === 'Em Progresso' ? 'bg-yellow-100 text-yellow-700' :
            'bg-green-100 text-green-700'
          }`}>
            {task.status || 'A Fazer'}
          </span>
        </div>
      </div>
    );
  };

  // Colunas do Kanban: tarefas de hoje ou que ficaram por fazer ontem (arrasto)
  const kanbanColumns = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const forToday = (task) => {
      const hasDate = task.date != null && String(task.date).trim() !== '';
      const completed = task.isCompleted || task.status === 'Concluído';
      const completedStr = typeof task.completedDate === 'string' ? String(task.completedDate).slice(0, 10) : null;

      if (!hasDate) return true;
      if (shouldShowTaskOnDate(task, todayStr)) return true;
      if (completed && completedStr === todayStr) return true;
      return false;
    };
    let list = tasks.filter(forToday);
    if (searchFilter) {
      const f = searchFilter.toLowerCase();
      list = list.filter(t => t.name?.toLowerCase().includes(f) || t.notes?.toLowerCase().includes(f) || t.status?.toLowerCase().includes(f));
    }
    const done = list.filter(t => isOccurrenceCompleted(t, todayStr));
    const notDoneToday = list.filter(t => !isOccurrenceCompleted(t, todayStr));
    const todo = notDoneToday.filter(t => t.status === 'A Fazer' || (!t.status && !t.isCompleted));
    const progress = notDoneToday.filter(t => t.status === 'Em Progresso');
    const fixedStart = [{ id: 'todo', title: 'A Fazer', status: 'A Fazer', tasks: todo, isFixed: true }];
    const middle = customColumns.map(col => ({
      id: col.id,
      title: col.title,
      status: col.status,
      tasks: notDoneToday.filter(t => t.status === col.status),
      isFixed: false
    }));
    if (!customColumns.find(c => c.status === 'Em Progresso')) {
      middle.push({ id: 'progress', title: 'Em Progresso', status: 'Em Progresso', tasks: progress, isFixed: false });
    }
    const fixedEnd = [{ id: 'done', title: 'Concluído', status: 'Concluído', tasks: done, isFixed: true }];
    return [...fixedStart, ...middle, ...fixedEnd];
  }, [tasks, searchFilter, customColumns]);

  // Para Lista e outras vistas: usar filteredTasks
  const tasksForGrouping = (viewType === 'LIST') ? tasksForKanbanAndList : filteredTasks;

  // Adicionar nova coluna
  const handleAddColumn = (e) => {
    e.preventDefault();
    if (!newColumnName.trim()) return;
    
    const newColumn = {
      id: `custom-${Date.now()}`,
      title: newColumnName.trim(),
      status: newColumnName.trim()
    };
    
    const updated = [...customColumns, newColumn];
    setCustomColumns(updated);
    
    // Guardar no localStorage
    try {
      localStorage.setItem(`kanban-columns-${currentCompany?.id}`, JSON.stringify(updated));
    } catch (err) {
      console.error('Erro ao guardar colunas:', err);
    }
    
    setNewColumnName('');
    setShowColumnModal(false);
  };

  // Remover coluna customizada
  const handleRemoveColumn = (columnId) => {
    const updated = customColumns.filter(c => c.id !== columnId);
    setCustomColumns(updated);
    
    // Atualizar tarefas dessa coluna para "A Fazer"
    setTasks(prev => prev.map(t => {
      const column = customColumns.find(c => c.id === columnId);
      if (column && t.status === column.status) {
        return { ...t, status: 'A Fazer' };
      }
      return t;
    }));
    
    // Guardar no localStorage
    try {
      localStorage.setItem(`kanban-columns-${currentCompany?.id}`, JSON.stringify(updated));
    } catch (err) {
      console.error('Erro ao guardar colunas:', err);
    }
  };

  // Navegação de datas
  const handleDateNavigation = (direction) => {
    if (viewType === 'DAY') {
      setCurrentDate(prev => direction === 'prev' ? subDays(prev, 1) : addDays(prev, 1));
    } else if (viewType === 'MONTH') {
      setCurrentDate(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1));
    } else if (viewType === 'YEAR') {
      setCurrentDate(prev => direction === 'prev' ? subYears(prev, 1) : addYears(prev, 1));
    }
  };

  // Formatar data para exibição
  const getDateDisplay = () => {
    if (viewType === 'DAY') {
      return formatPtDayMonth(currentDate, { shortMonth: false });
    } else if (viewType === 'WEEK') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${formatPtDayMonth(weekStart, { shortMonth: true })} - ${formatPtDayMonth(weekEnd, { shortMonth: true })}`;
    } else if (viewType === 'MONTH') {
      return formatPtMonthYear(currentDate);
    } else if (viewType === 'YEAR') {
      return formatYear(currentDate);
    }
    return '';
  };

  // Abrir modal para nova tarefa
  const handleNewTask = () => {
    resetForm();
    setShowTaskModal(true);
  };

  // Abrir modal para visualizar tarefa
  const handleViewTask = (task) => {
    setViewingTask(task);
  };

  // Atualizar status da tarefa (conclusão por ocorrência em tarefas recorrentes)
  const handleStatusChange = (task, newStatus) => {
    if (!setTasks || !currentCompany) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const markingCompleted = newStatus === 'Concluído';
    const isRecurring = task.autoSchedule && task.repeatType;

    setTasks(prev => prev.map(t => {
      if (t.id !== task.id) return t;
      const updated = { ...t, companyId: currentCompany.id };
      if (isRecurring) {
        const list = Array.isArray(t.completedOccurrences) ? [...t.completedOccurrences] : [];
        if (markingCompleted) {
          if (!list.includes(today)) list.push(today);
          updated.completedOccurrences = list;
        } else {
          updated.completedOccurrences = list.filter(d => d !== today);
        }
        updated.status = newStatus;
        updated.isCompleted = false;
        delete updated.completedDate;
      } else {
        updated.status = newStatus;
        updated.isCompleted = markingCompleted;
        if (markingCompleted) updated.completedDate = today;
        else delete updated.completedDate;
      }
      return updated;
    }));

    if (markingCompleted) {
      setViewingTask(prev => {
        if (!prev) return null;
        if (isRecurring) {
          const list = [...(prev.completedOccurrences || [])];
          if (!list.includes(today)) list.push(today);
          return { ...prev, completedOccurrences: list };
        }
        return { ...prev, status: newStatus, isCompleted: true, completedDate: today };
      });
    } else {
      setViewingTask(prev => prev ? { ...prev, completedOccurrences: (prev.completedOccurrences || []).filter(d => d !== today), status: newStatus, isCompleted: false } : null);
    }
  };

  // Abrir modal para editar tarefa a partir da visualização
  const handleEditFromView = (task) => {
    setViewingTask(null);
    setEditingTask(task);
    
    // Arredondar automaticamente o horário de início ao abrir a tarefa
    const taskStartTime = task.startTime || '09:00';
    const { startTime: roundedStart, endTime: calculatedEnd } = roundAndSetDuration(taskStartTime);
    
    setTaskForm({
      name: task.name || '',
      notes: task.notes || '',
      date: task.date || format(new Date(), 'yyyy-MM-dd'),
      startTime: roundedStart,
      endTime: task.endTime || calculatedEnd,
      assignedTo: task.assignedTo || currentUser?.id || '',
      status: task.status || 'A Fazer',
      autoSchedule: !!task.autoSchedule,
      repeatType: task.repeatType || 'DAILY',
      repeatIntervalDays: typeof task.repeatIntervalDays === 'number' ? task.repeatIntervalDays : 1
    });
    setShowTaskModal(true);
  };

  // Salvar tarefa (conclusão por ocorrência em recorrentes)
  const handleSaveTask = (e) => {
    e.preventDefault();
    const today = format(new Date(), 'yyyy-MM-dd');
    const isRecurring = taskForm.autoSchedule && taskForm.repeatType;
    const markingCompleted = taskForm.status === 'Concluído';

    if (editingTask) {
      setTasks(prev => prev.map(t => {
        if (t.id !== editingTask.id) return t;
        const updated = { ...t, ...taskForm, companyId: currentCompany.id };
        if (isRecurring) {
          updated.isCompleted = false;
          delete updated.completedDate;
          const list = Array.isArray(updated.completedOccurrences) ? [...updated.completedOccurrences] : [];
          if (markingCompleted && !list.includes(today)) list.push(today);
          else if (!markingCompleted) updated.completedOccurrences = list.filter(d => d !== today);
          else updated.completedOccurrences = list;
        } else {
          updated.isCompleted = markingCompleted;
          if (markingCompleted) updated.completedDate = today;
          else delete updated.completedDate;
        }
        return updated;
      }));
    } else {
      const newTask = {
        id: `task-${Date.now()}`,
        companyId: currentCompany.id,
        ...taskForm,
        isCompleted: isRecurring ? false : markingCompleted,
        ...(isRecurring && markingCompleted ? { completedOccurrences: [today] } : !isRecurring && markingCompleted ? { completedDate: today } : {})
      };
      setTasks(prev => [...prev, newTask]);
    }
    setShowTaskModal(false);
    resetForm();
  };

  // Eliminar tarefa
  const handleDeleteTask = () => {
    if (!editingTask) return;
    
    if (window.confirm('Tem certeza que deseja eliminar esta tarefa? Esta ação não pode ser desfeita.')) {
      setTasks(prev => prev.filter(t => t.id !== editingTask.id));
      setShowTaskModal(false);
      resetForm();
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    setDraggedOverColumn(columnId);
  };

  const handleDrop = (e, column) => {
    e.preventDefault();
    if (!draggedTask) {
      setDraggedTask(null);
      setDraggedOverColumn(null);
      return;
    }
    const newStatus = column.status;
    const markingCompleted = newStatus === 'Concluído';
    const today = format(new Date(), 'yyyy-MM-dd');
    const isRecurring = draggedTask.autoSchedule && draggedTask.repeatType;

    setTasks(prev => prev.map(t => {
      if (t.id !== draggedTask.id) return t;
      const updated = { ...t, status: newStatus, companyId: currentCompany.id };
      if (isRecurring) {
        const list = Array.isArray(t.completedOccurrences) ? [...t.completedOccurrences] : [];
        if (markingCompleted) {
          if (!list.includes(today)) list.push(today);
          updated.completedOccurrences = list;
        } else {
          updated.completedOccurrences = list.filter(d => d !== today);
        }
        updated.isCompleted = false;
        delete updated.completedDate;
      } else {
        updated.isCompleted = markingCompleted;
        if (markingCompleted) updated.completedDate = today;
        else delete updated.completedDate;
      }
      return updated;
    }));
    setDraggedTask(null);
    setDraggedOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDraggedOverColumn(null);
  };

  return (
    <div className="min-h-screen bg-[#f1f8e9]">
      <div className="max-w-[1400px] mx-auto p-6 space-y-6">
        {/* Header com título e seletor de visualização */}
        <div className="flex flex-col gap-6">
          {/* Título */}
          <div className="flex items-center gap-4">
            {currentUser?.userType === UserType.SIMPLE && onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-white rounded-xl transition-colors bg-white shadow-sm border border-slate-200"
              >
                <ArrowLeft size={24} className="text-slate-600" />
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Tarefas</h1>
              <p className="text-sm text-slate-500 mt-1">Gestão de tarefas e operações</p>
              {viewType === 'KANBAN' && (
                <p className="text-xs font-semibold text-[#1b5e20] mt-1" aria-live="polite">
                  Kanban: apenas tarefas do dia {format(new Date(), 'dd/MM/yyyy')}
                </p>
              )}
            </div>
          </div>
          
          {/* Seletor de visualização */}
          <div className="flex items-center justify-between gap-2">
            {/* Botões principais centralizados */}
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl p-1 shadow-sm">
                <button
                  onClick={() => setViewType('KANBAN')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold text-sm ${
                    viewType === 'KANBAN'
                      ? 'bg-[#1b5e20] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Kanban
                </button>
                <button
                  onClick={() => setViewType('DAY')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold text-sm ${
                    viewType === 'DAY'
                      ? 'bg-[#1b5e20] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Dia
                </button>
                <button
                  onClick={() => setViewType('WEEK')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold text-sm ${
                    viewType === 'WEEK'
                      ? 'bg-[#1b5e20] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Semana
                </button>
                <button
                  onClick={() => setViewType('MONTH')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold text-sm ${
                    viewType === 'MONTH'
                      ? 'bg-[#1b5e20] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Mês
                </button>
                <button
                  onClick={() => setViewType('YEAR')}
                  className={`px-4 py-2 rounded-xl transition-all font-bold text-sm ${
                    viewType === 'YEAR'
                      ? 'bg-[#1b5e20] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Ano
                </button>
              </div>
            </div>
            {/* Lista e Histórico em formato horizontal */}
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl p-1 shadow-sm">
              <button
                onClick={() => setViewType('LIST')}
                title="Lista"
                className={`p-3 rounded-xl transition-all ${
                  viewType === 'LIST'
                    ? 'bg-[#1b5e20] text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <List size={20} />
              </button>
              <button
                onClick={() => setViewType('HISTORY')}
                title="Histórico (Concluídas)"
                className={`p-3 rounded-xl transition-all ${
                  viewType === 'HISTORY'
                    ? 'bg-[#1b5e20] text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <History size={20} />
              </button>
            </div>
          </div>

          {/* Navegação de datas (para outras visualizações) */}
          {(viewType === 'DAY' || viewType === 'WEEK' || viewType === 'MONTH' || viewType === 'YEAR') && (
            <div className="flex items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleDateNavigation('prev')}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors text-slate-400 hover:text-slate-600"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="font-bold text-slate-900 text-lg min-w-[200px] text-center">
                  {getDateDisplay()}
                </span>
                <button
                  onClick={() => handleDateNavigation('next')}
                  className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors text-slate-400 hover:text-slate-600"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              {viewType === 'YEAR' && (
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-colors border border-slate-200"
                >
                  Hoje
                </button>
              )}
            </div>
          )}
        </div>

        {/* Container branco com barra de pesquisa e botões */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
          {/* Barra de pesquisa e botões de ação */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Barra de pesquisa */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Pesquisar tarefas..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
              />
            </div>

            {/* Botão Tabela (Adicionar Coluna) */}
            <button
              onClick={() => setShowColumnModal(true)}
              className="bg-white border border-slate-200 rounded-xl px-4 py-3 font-bold text-[#1b5e20] flex items-center gap-2 hover:bg-slate-50 transition-colors shadow-sm"
            >
              <div className="w-8 h-8 rounded-full bg-[#1b5e20] flex items-center justify-center">
                <Plus size={16} className="text-white" />
              </div>
              Tabela
            </button>

            {/* Botão Nova Tarefa */}
            <button
              onClick={handleNewTask}
              className="bg-[#1b5e20] text-white rounded-xl px-6 py-3 font-bold flex items-center gap-2 hover:bg-[#15501a] transition-colors shadow-md"
            >
              <Plus size={20} />
              Nova Tarefa
            </button>
          </div>

          {/* Conteúdo principal - Kanban */}
          {viewType === 'KANBAN' && (
            <div className={`grid grid-cols-1 md:grid-cols-2 ${
              kanbanColumns.length <= 3 ? 'lg:grid-cols-3' : 
              kanbanColumns.length === 4 ? 'lg:grid-cols-4' : 
              kanbanColumns.length === 5 ? 'lg:grid-cols-5' : 
              'lg:grid-cols-6'
            } gap-6`}>
              {kanbanColumns.map((column) => (
                <div
                  key={column.id}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDrop={(e) => handleDrop(e, column)}
                  className={`bg-white rounded-xl border-2 p-5 min-h-[400px] transition-colors ${
                    draggedOverColumn === column.id ? 'border-[#1b5e20] bg-green-50' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 text-base truncate">{column.title}</h3>
                      {!column.isFixed && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Tem certeza que deseja remover a coluna "${column.title}"? As tarefas serão movidas para "A Fazer".`)) {
                              handleRemoveColumn(column.id);
                            }
                          }}
                          className="text-slate-400 hover:text-red-600 transition-colors p-1"
                          title="Remover coluna"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0">
                      {column.tasks.length}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    {column.tasks.length > 0 ? (
                      column.tasks.map((task) => (
                        <div
                          key={task.id}
                          draggable
                          onDragStart={() => handleDragStart(task)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleViewTask(task)}
                          className={`bg-slate-50 border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all cursor-move ${
                            draggedTask?.id === task.id ? 'opacity-50' : ''
                          }`}
                        >
                          <p className="font-semibold text-slate-900 mb-2">{task.name}</p>
                          {task.notes && (
                            <p className="text-sm text-slate-500 mb-3 line-clamp-2">{task.notes}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                            {task.date && (() => {
                              try {
                                return (
                                  <div className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    <span>{format(parse(task.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')}</span>
                                  </div>
                                );
                              } catch {
                                return null;
                              }
                            })()}
                            {task.startTime && (
                              <div className="flex items-center gap-1">
                                <Clock size={12} />
                                <span>{task.endTime ? `${task.startTime} - ${task.endTime}` : task.startTime}</span>
                              </div>
                            )}
                            {task.assignedTo && (
                              <div className="flex items-center gap-1">
                                <User size={12} />
                                <span>{safeTeam.find(u => u.id === task.assignedTo)?.name || 'Não atribuído'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 text-sm text-center py-8">Nenhuma tarefa</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Visualização de lista */}
          {viewType === 'LIST' && (
            <div className="space-y-3">
              {tasksForGrouping.length > 0 ? (
                tasksForGrouping.map((task) => <TaskCard key={task.id} task={task} />)
              ) : (
                <p className="text-slate-400 text-sm text-center py-8">Nenhuma tarefa encontrada</p>
              )}
            </div>
          )}

          {/* Visualização Dia */}
          {viewType === 'DAY' && (() => {
            // Timeline reduzida: das 6:00 às 20:00 (14 horas)
            const startHour = 6;
            const endHour = 20;
            const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => {
              const hour = startHour + i;
              return `${String(hour).padStart(2, '0')}:00`;
            });

            // Tarefas do dia atual (incluir tarefas sem data como se fossem do dia em vista)
            const dayStr = format(currentDate, 'yyyy-MM-dd');
            const dayTasks = filteredTasks.filter(task => {
              if (!task.date) return true;
              return shouldShowTaskOnDate(task, dayStr);
            }).sort(sortByTime);

            // Verificar se há feriado neste dia
            const holiday = getHolidayForDate(currentDate);

            return (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {/* Feriado - nota no topo */}
                {holiday && (
                  <div className="px-6 pt-4 pb-2">
                    <div className="bg-purple-100 border border-purple-200 rounded-lg px-4 py-2">
                      <p className="text-sm font-semibold text-purple-700 capitalize">
                        🎉 {holiday.name}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Cabeçalho do dia */}
                <div className={`p-6 border-b border-slate-200 bg-slate-50 ${holiday ? 'pt-2' : ''}`}>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' }).format(currentDate)}
                  </h2>
                  <p className="text-sm font-semibold text-slate-600 mt-1">
                    {formatPtWeekday(currentDate)}
                  </p>
                </div>

                {/* Timeline */}
                <div className="relative">
                  {/* Seção "Dia Todo" */}
                  <div className="border-b border-slate-200 p-4 bg-slate-50">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Dia Todo</p>
                    <div className="h-8 mt-2"></div>
                  </div>

                  {/* Timeline com horários (6:00 - 20:00) */}
                  <div className="relative" style={{ minHeight: `${(endHour - startHour + 1) * 60}px` }}>
                    {/* Linhas de hora (background) */}
                    {hours.map((hour, index) => {
                      return (
                        <div
                          key={hour}
                          className="absolute left-0 right-0 border-b border-slate-100 flex"
                          style={{ top: `${index * 60}px`, height: '60px' }}
                        >
                          {/* Label de hora */}
                          <div className="w-20 px-4 py-2 flex-shrink-0 border-r border-slate-100">
                            <span className="text-xs font-semibold text-slate-500">{hour}</span>
                          </div>
                          {/* Área de tarefas (vazia, as tarefas são renderizadas absolutamente) */}
                          <div className="flex-1 relative pl-4 pr-4"></div>
                        </div>
                      );
                    })}

                    {/* Tarefas posicionadas absolutamente */}
                    <div className="absolute left-20 right-0 top-0 bottom-0 pl-4 pr-4">
                      {dayTasks.map(task => {
                        const { top, height } = getTaskPosition(task, startHour);
                        
                        // Só mostrar tarefas que estão dentro do horário visível (6:00 - 20:00)
                        const taskStartMinutes = timeToMinutes(task.startTime || '00:00');
                        const taskStartHour = Math.floor(taskStartMinutes / 60);
                        
                        if (taskStartHour < startHour || taskStartHour > endHour) {
                          return null;
                        }
                        
                        // Ajustar posição relativa ao início da timeline (6:00)
                        const adjustedTop = top - (startHour * 60);
                        
                        return (
                          <div
                            key={task.id}
                            onClick={() => handleViewTask(task)}
                            className="absolute left-0 right-0 rounded-lg border-2 cursor-pointer hover:shadow-md transition-shadow z-10"
                            style={{
                              top: `${adjustedTop}px`,
                              height: `${height}px`,
                              backgroundColor: task.status === 'Concluído' || task.isCompleted 
                                ? '#dcfce7' 
                                : task.status === 'Em Progresso'
                                ? '#fef3c7'
                                : '#dbeafe',
                              borderColor: task.status === 'Concluído' || task.isCompleted
                                ? '#86efac'
                                : task.status === 'Em Progresso'
                                ? '#fcd34d'
                                : '#93c5fd'
                            }}
                          >
                            <div className="p-2 h-full flex flex-col justify-start">
                              <p className="text-sm font-bold text-slate-900 break-words leading-snug mb-1">
                                {task.name || 'Sem título'}
                              </p>
                              <p className="text-[10px] text-slate-600 whitespace-nowrap">
                                {task.startTime || '00:00'} - {task.endTime || task.startTime || '01:00'}
                              </p>
                              {task.assignedTo && (
                                <p className="text-[10px] text-slate-500 mt-auto whitespace-nowrap">
                                  {getAssignedName(task)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {dayTasks.length === 0 && (
                  <div className="p-8 text-center">
                    <p className="text-slate-400 text-sm">Nenhuma tarefa para este dia</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Visualização Semana */}
          {viewType === 'WEEK' && (() => {
            const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
            const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
            
            // Função para determinar se uma tarefa é manhã (antes de 13:00) ou tarde
            const isMorning = (task) => {
              if (!task.startTime) return false;
              const [hours] = task.startTime.split(':').map(Number);
              return hours < 13;
            };
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
                {days.map((day) => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const dayTasks = filteredTasks
                    .filter(t => {
                      if (!t.date) return isSameDay(day, new Date());
                      return shouldShowTaskOnDate(t, dayStr);
                    })
                    .sort(sortByTime);
                  
                  const morningTasks = dayTasks.filter(isMorning);
                  const afternoonTasks = dayTasks.filter(t => !isMorning(t));
                  const totalTasks = dayTasks.length;
                  
                  // Verificar se há feriado neste dia
                  const holiday = getHolidayForDate(day);
                  
                  return (
                    <div key={day.toISOString()} className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col min-h-[500px]">
                      {/* Cabeçalho do dia */}
                      <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50 rounded-t-xl">
                        <div className="min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            {formatPtWeekday(day, { short: true })}
                          </p>
                          <p className="font-black text-slate-900 text-sm">{format(day, 'dd/MM')}</p>
                        </div>
                        <span className="bg-[#1b5e20] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                          {totalTasks}
                        </span>
                      </div>
                      
                      {/* Feriado - entre o dia e a manhã, como se fosse uma tarefa */}
                      {holiday && (
                        <div className="px-3 py-2 border-b border-purple-100 bg-purple-50">
                          <div className="bg-purple-100 border border-purple-200 rounded-lg px-3 py-2">
                            <p className="text-xs font-semibold text-purple-700 truncate">
                              🎉 {holiday.name}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Conteúdo dividido em Manhã e Tarde */}
                      <div className="flex-1 flex flex-col">
                        {/* Manhã - Parte Superior */}
                        <div className="flex-1 flex flex-col border-b border-slate-200">
                          <div className="px-3 py-2 bg-amber-50 border-b border-amber-100">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                              Manhã {morningTasks.length > 0 && `(${morningTasks.length})`}
                            </p>
                          </div>
                          <div className="flex-1 p-2 space-y-1.5 overflow-y-auto min-h-[200px] max-h-[250px]">
                            {morningTasks.length > 0 ? (
                              morningTasks.map(task => <TaskCard key={task.id} task={task} ultraCompact />)
                            ) : (
                              <p className="text-slate-300 text-xs text-center py-8">—</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Tarde - Parte Inferior */}
                        <div className="flex-1 flex flex-col">
                          <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700">
                              Tarde {afternoonTasks.length > 0 && `(${afternoonTasks.length})`}
                            </p>
                          </div>
                          <div className="flex-1 p-2 space-y-1.5 overflow-y-auto min-h-[200px] max-h-[250px]">
                            {afternoonTasks.length > 0 ? (
                              afternoonTasks.map(task => <TaskCard key={task.id} task={task} ultraCompact />)
                            ) : (
                              <p className="text-slate-300 text-xs text-center py-8">—</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Visualização Mês - Calendário Mensal */}
          {viewType === 'MONTH' && (() => {
            // Obter início e fim do mês
            const monthStart = startOfMonth(currentDate);
            const monthEnd = endOfMonth(currentDate);
            
            // Começar na segunda-feira (weekStartsOn: 1)
            const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
            const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
            
            // Gerar todos os dias do calendário
            const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
            
            // Agrupar tarefas por dia (recorrência: cada tarefa aparece em cada dia em que ocorre)
            const today = new Date();
            const todayDateKeyForMonth = format(today, 'yyyy-MM-dd');
            const tasksByDate = {};
            calendarDays.forEach(day => { tasksByDate[format(day, 'yyyy-MM-dd')] = []; });
            filteredTasks.forEach(task => {
              if (!task.date) {
                tasksByDate[todayDateKeyForMonth].push(task);
                return;
              }
              calendarDays.forEach(day => {
                const key = format(day, 'yyyy-MM-dd');
                if (tasksByDate[key] && shouldShowTaskOnDate(task, key)) tasksByDate[key].push(task);
              });
            });

            // Nomes dos dias da semana
            const weekDays = ['seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.', 'dom.'];
            const todayDateKey = todayDateKeyForMonth;
            const isCurrentMonth = isSameMonth(currentDate, today);
            const currentMonthNum = currentDate.getMonth();
            const currentYear = currentDate.getFullYear();

            return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Cabeçalho do calendário */}
                <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-slate-900 capitalize">
                    {formatPtMonthYear(currentDate)}
                  </h2>
                </div>

                {/* Dias da semana */}
                <div className="grid grid-cols-7 border-b border-slate-200">
                  {weekDays.map((day, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 text-center text-sm font-semibold text-slate-600 border-r border-slate-100 last:border-r-0"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Grid de dias */}
                <div className="grid grid-cols-7">
                  {calendarDays.map((day, idx) => {
                    const dayMonth = day.getMonth();
                    const dayYear = day.getFullYear();
                    const isCurrentMonthDay = dayMonth === currentMonthNum && dayYear === currentYear;
                    const dayDateKey = format(day, 'yyyy-MM-dd');
                    const dayTasks = tasksByDate[dayDateKey] || [];
                    const isToday = isCurrentMonth && dayDateKey === todayDateKey;
                    const dayNumber = day.getDate();
                    
                    // Verificar se há feriado neste dia
                    const holiday = getHolidayForDate(day);
                    
                    // Limitar número de tarefas a mostrar (se houver feriado, mostrar uma tarefa a menos)
                    const maxTasks = holiday ? 3 : 4;

                    return (
                      <div
                        key={idx}
                        onClick={() => {
                          setCurrentDate(day);
                          setViewType('DAY');
                        }}
                        className={`
                          min-h-[140px] bg-white p-2 cursor-pointer transition-colors border-r border-b border-slate-100
                          ${idx % 7 === 6 ? 'border-r-0' : ''}
                          ${!isCurrentMonthDay ? 'opacity-40 bg-slate-50' : ''}
                          ${isToday ? 'ring-2 ring-[#1b5e20] bg-green-50' : isCurrentMonthDay ? 'hover:bg-slate-50' : ''}
                        `}
                      >
                        {/* Número do dia */}
                        <div className={`
                          text-sm font-semibold mb-1
                          ${isToday ? 'text-[#1b5e20] font-bold' : isCurrentMonthDay ? 'text-slate-700' : 'text-slate-400'}
                        `}>
                          {dayNumber}
                        </div>

                        {/* Tarefas do dia */}
                        <div className="space-y-0.5 overflow-hidden">
                          {/* Feriado - mostrar primeiro, em roxo */}
                          {holiday && (
                            <div className="text-[10px] px-1.5 py-0.5 rounded truncate whitespace-nowrap bg-purple-100 text-purple-700 font-medium">
                              {holiday.name}
                            </div>
                          )}
                          
                          {dayTasks.slice(0, maxTasks).map((task, taskIdx) => {
                            const statusColor = 
                              task.status === 'Concluído' || task.isCompleted
                                ? 'bg-green-100 text-green-700'
                                : task.status === 'Em Progresso'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-blue-100 text-blue-700';
                            
                            return (
                              <div
                                key={task.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewTask(task);
                                }}
                                className={`
                                  text-[10px] px-1.5 py-0.5 rounded truncate whitespace-nowrap
                                  ${statusColor}
                                  hover:opacity-80 transition-opacity cursor-pointer font-medium
                                `}
                                title={`${task.startTime || ''} ${task.name || 'Sem título'}`}
                              >
                                {task.name || 'Sem título'}
                              </div>
                            );
                          })}
                          {dayTasks.length > maxTasks && (
                            <div className="text-[10px] text-slate-500 font-semibold px-1.5 py-0.5">
                              +{dayTasks.length - maxTasks} mais
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Visualização Ano - Calendário Anual */}
          {viewType === 'YEAR' && (() => {
            const year = currentDate.getFullYear();
            const today = new Date();
            const todayDateKeyYear = format(today, 'yyyy-MM-dd');

            // Função para gerar dias do calendário mensal
            const getMonthDays = (monthIndex) => {
              const monthDate = new Date(year, monthIndex, 1);
              const start = startOfMonth(monthDate);
              const end = endOfMonth(monthDate);
              const calendarStart = startOfWeek(start, { weekStartsOn: 1 });
              const calendarEnd = endOfWeek(end, { weekStartsOn: 1 });
              return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
            };

            // Agrupar tarefas por dia (recorrência: cada tarefa em cada dia em que ocorre)
            const tasksByDate = {};
            const yearStart = new Date(year, 0, 1);
            const yearEnd = new Date(year, 11, 31);
            for (let d = yearStart; d <= yearEnd; d = addDays(d, 1)) {
              tasksByDate[format(d, 'yyyy-MM-dd')] = [];
            }
            filteredTasks.forEach(task => {
              if (!task.date) {
                if (tasksByDate[todayDateKeyYear]) tasksByDate[todayDateKeyYear].push(task);
                return;
              }
              for (let d = yearStart; d <= yearEnd; d = addDays(d, 1)) {
                const key = format(d, 'yyyy-MM-dd');
                if (tasksByDate[key] && shouldShowTaskOnDate(task, key)) tasksByDate[key].push(task);
              }
            });

            // Nomes dos meses e dias da semana
            const monthNames = [
              'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
              'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
            ];
            const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
            
            const isTodayInYear = today.getFullYear() === year;
            const todayDateKey = todayDateKeyYear;

            return (
              <div className="space-y-6">
                {/* Grid de 12 meses (3 colunas x 4 linhas) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {monthNames.map((monthName, monthIndex) => {
                    const days = getMonthDays(monthIndex);
                    const monthDate = new Date(year, monthIndex, 1);
                    const currentMonth = monthDate.getMonth();
                    const currentYear = monthDate.getFullYear();

                    return (
                      <div key={monthIndex} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                        {/* Título do mês */}
                        <h3 className="text-sm font-bold text-red-600 mb-3 capitalize">
                          {monthName}
                        </h3>

                        {/* Cabeçalho dos dias da semana */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {weekDays.map((day, idx) => (
                            <div
                              key={idx}
                              className="text-center text-xs font-semibold text-slate-500 py-1"
                            >
                              {day}
                            </div>
                          ))}
                        </div>

                        {/* Dias do calendário */}
                        <div className="grid grid-cols-7 gap-1">
                          {days.map((day, idx) => {
                            const dayMonth = day.getMonth();
                            const dayYear = day.getFullYear();
                            const isCurrentMonth = dayMonth === currentMonth && dayYear === currentYear;
                            const dayDateKey = format(day, 'yyyy-MM-dd');
                            const dayTasks = tasksByDate[dayDateKey] || [];
                            const hasTasks = dayTasks.length > 0;
                            const isToday = isTodayInYear && dayDateKey === todayDateKey;
                            const dayNumber = day.getDate();
                            
                            // Verificar se há feriado neste dia
                            const holiday = getHolidayForDate(day);

                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  setCurrentDate(day);
                                  setViewType('DAY');
                                }}
                                className={`
                                  relative aspect-square p-1 rounded-lg text-xs font-semibold transition-all
                                  ${!isCurrentMonth ? 'text-slate-300' : ''}
                                  ${holiday && isCurrentMonth ? 'bg-purple-100 text-purple-700 font-bold' : ''}
                                  ${!holiday && !isCurrentMonth ? 'text-slate-300' : !holiday ? 'text-slate-700' : ''}
                                  ${isToday && !holiday ? 'ring-2 ring-[#1b5e20] bg-green-50 font-bold' : ''}
                                  ${hasTasks && isCurrentMonth && !isToday && !holiday ? 'bg-green-50 hover:bg-green-100' : isCurrentMonth && !isToday && !holiday ? 'hover:bg-slate-50' : ''}
                                  ${isCurrentMonth ? '' : 'opacity-50'}
                                `}
                                title={holiday ? holiday.name : hasTasks ? `${dayTasks.length} tarefa(s)` : ''}
                              >
                                <span className="flex items-center justify-center w-full h-full">
                                  {dayNumber}
                                </span>
                                {hasTasks && isCurrentMonth && !holiday && (
                                  <span className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#1b5e20] rounded-full"></span>
                                )}
                                {holiday && isCurrentMonth && (
                                  <span className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-purple-600 rounded-full"></span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Histórico */}
          {viewType === 'HISTORY' && (
            <div className="space-y-3">
              {filteredTasks.length > 0 ? (
                [...filteredTasks].sort((a, b) => {
                  const da = parseTaskDate(a);
                  const db = parseTaskDate(b);
                  if (da && db) return db.getTime() - da.getTime();
                  return 0;
                }).map((task) => <TaskCard key={task.id} task={task} />)
              ) : (
                <p className="text-slate-400 text-sm text-center py-8">Nenhuma tarefa concluída</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de visualizar tarefa */}
      {viewingTask && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Detalhes da Tarefa</h2>
              <button
                type="button"
                onClick={() => setViewingTask(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-500 block mb-1">Nome da Tarefa</label>
                <p className="text-lg font-semibold text-slate-900">{viewingTask.name || 'Sem título'}</p>
              </div>

              {viewingTask.notes && (
                <div>
                  <label className="text-sm font-bold text-slate-500 block mb-1">Notas</label>
                  <p className="text-base text-slate-700 whitespace-pre-wrap">{viewingTask.notes}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {viewingTask.date && (
                  <div>
                    <label className="text-sm font-bold text-slate-500 block mb-1">Data</label>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-slate-400" />
                      <p className="text-base text-slate-900">
                        {(() => {
                          try {
                            const taskDate = parse(viewingTask.date, 'yyyy-MM-dd', new Date());
                            return formatDDMMYYYY(taskDate);
                          } catch {
                            return viewingTask.date;
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                )}

                {(viewingTask.startTime || viewingTask.endTime) && (
                  <div>
                    <label className="text-sm font-bold text-slate-500 block mb-1">Horário</label>
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-slate-400" />
                      <p className="text-base text-slate-900">
                        {viewingTask.startTime || '—'}
                        {viewingTask.endTime ? ` - ${viewingTask.endTime}` : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {viewingTask.assignedTo && (
                  <div>
                    <label className="text-sm font-bold text-slate-500 block mb-1">Atribuído a</label>
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400" />
                      <p className="text-base text-slate-900">{getAssignedName(viewingTask)}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-bold text-slate-500 block mb-1">Status {viewingTask.autoSchedule && viewingTask.repeatType && '(esta ocorrência)'}</label>
                  <select
                    value={(() => {
                      const todayStr = format(new Date(), 'yyyy-MM-dd');
                      if (viewingTask.autoSchedule && viewingTask.repeatType && isOccurrenceCompleted(viewingTask, todayStr)) return 'Concluído';
                      return viewingTask.status || 'A Fazer';
                    })()}
                    onChange={(e) => handleStatusChange(viewingTask, e.target.value)}
                    className={`w-full p-3 border-2 rounded-xl font-bold text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition-colors ${
                      (() => {
                        const todayStr = format(new Date(), 'yyyy-MM-dd');
                        const done = (viewingTask.autoSchedule && viewingTask.repeatType) ? isOccurrenceCompleted(viewingTask, todayStr) : (viewingTask.isCompleted || viewingTask.status === 'Concluído');
                        if (done) return 'bg-green-100 text-green-700 border-green-300';
                        if (viewingTask.status === 'Em Progresso') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
                        return 'bg-blue-100 text-blue-700 border-blue-300';
                      })()
                    }`}
                  >
                    <option value="A Fazer">A Fazer</option>
                    <option value="Em Progresso">Em Progresso</option>
                    <option value="Concluído">Concluído</option>
                  </select>
                </div>
              </div>

              {viewingTask.autoSchedule && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="text-sm font-bold text-slate-500 block mb-2">Agendamento Automático</label>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-700">
                      <span className="font-semibold">Frequência:</span>{' '}
                      {viewingTask.repeatType === 'DAILY' && 'Todos os dias'}
                      {viewingTask.repeatType === 'EVERY_OTHER_DAY' && 'Dia sim, dia não'}
                      {viewingTask.repeatType === 'WEEKLY' && 'De semana em semana'}
                      {viewingTask.repeatType === 'MONTHLY' && 'De mês em mês'}
                      {viewingTask.repeatType === 'CUSTOM_DAYS' && `A cada ${viewingTask.repeatIntervalDays || 1} dia(s)`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setViewingTask(null)}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => handleEditFromView(viewingTask)}
                className="flex-1 py-3 bg-[#1b5e20] text-white rounded-xl font-bold hover:bg-[#15501a] transition-colors flex items-center justify-center gap-2"
              >
                <Edit size={18} />
                Editar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de criar/editar tarefa */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <form onSubmit={handleSaveTask} className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl space-y-6 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingTask ? 'Editar Tarefa' : 'Agendar Nova Tarefa'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowTaskModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-700 block mb-2">Nome da Tarefa *</label>
                <input
                  type="text"
                  required
                  value={taskForm.name}
                  onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                  placeholder="Ex: Entrega para cliente X"
                />
              </div>

              <div>
                <label className="text-sm font-bold text-slate-700 block mb-2">Notas</label>
                <textarea
                  value={taskForm.notes}
                  onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                  placeholder="Detalhes adicionais..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Data *</label>
                  <input
                    type="date"
                    required
                    value={taskForm.date}
                    onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Horário Início</label>
                  <input
                    type="time"
                    value={taskForm.startTime}
                    onChange={(e) => {
                      // Sempre arredonda e calcula automaticamente quando muda o início
                      const newStartTime = e.target.value;
                      const { startTime: roundedStart, endTime: calculatedEnd } = roundAndSetDuration(newStartTime);
                      setTaskForm({ 
                        ...taskForm, 
                        startTime: roundedStart,
                        endTime: calculatedEnd
                      });
                    }}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Horário Fim</label>
                  <input
                    type="time"
                    value={taskForm.endTime}
                    onChange={(e) => {
                      // Permite edição manual
                      setTaskForm({ ...taskForm, endTime: e.target.value });
                    }}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-slate-700 block mb-2">Atribuído a</label>
                  <select
                    value={taskForm.assignedTo}
                    onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                    className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                  >
                    <option value="">Não atribuído</option>
                    {assignableTeam
                      .filter(u => u?.role === UserRole.EMPLOYEE || u?.role === UserRole.OWNER)
                      .map(user => (
                        <option key={user.id} value={user.id}>{user.name} ({user.role})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-700 font-bold text-sm uppercase tracking-wider">Agendamento Automático</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTaskForm(prev => ({ ...prev, autoSchedule: !prev.autoSchedule }))}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                      taskForm.autoSchedule ? 'bg-green-600' : 'bg-slate-300'
                    }`}
                    aria-pressed={taskForm.autoSchedule}
                    aria-label="Agendamento automático"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        taskForm.autoSchedule ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {taskForm.autoSchedule && (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="text-xs font-bold text-slate-500 block mb-1 uppercase tracking-wider">Frequência da repetição</label>
                      <select
                        value={taskForm.repeatType}
                        onChange={(e) => setTaskForm(prev => ({ ...prev, repeatType: e.target.value }))}
                        className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                      >
                        <option value="DAILY">Todos os dias</option>
                        <option value="EVERY_OTHER_DAY">Dia sim, dia não</option>
                        <option value="WEEKLY">De semana em semana</option>
                        <option value="MONTHLY">De mês em mês</option>
                        <option value="CUSTOM_DAYS">Personalizada (Por intervalo de dias)</option>
                      </select>
                    </div>

                    {taskForm.repeatType === 'CUSTOM_DAYS' && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-slate-600">Repetir a cada</span>
                        <input
                          type="number"
                          min={1}
                          value={taskForm.repeatIntervalDays}
                          onChange={(e) => setTaskForm(prev => ({ ...prev, repeatIntervalDays: Number(e.target.value || 1) }))}
                          className="w-28 p-3 border border-slate-200 rounded-xl bg-white text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                        />
                        <span className="text-sm font-bold text-slate-600">dias</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-bold text-slate-700 block mb-2">Status</label>
                <select
                  value={taskForm.status}
                  onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                  className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                >
                  <option value="A Fazer">A Fazer</option>
                  <option value="Em Progresso">Em Progresso</option>
                  {customColumns.map(col => (
                    <option key={col.id} value={col.status}>{col.title}</option>
                  ))}
                  <option value="Concluído">Concluído</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              {editingTask && (
                <button
                  type="button"
                  onClick={handleDeleteTask}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <Trash2 size={18} />
                  Eliminar
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowTaskModal(false);
                  resetForm();
                }}
                className={`${editingTask ? 'flex-1' : 'flex-1'} py-3 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 py-3 bg-[#1b5e20] text-white rounded-xl font-bold hover:bg-[#15501a] transition-colors"
              >
                {editingTask ? 'Atualizar' : 'Registar'} Tarefa
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal de criar nova coluna */}
      {showColumnModal && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <form onSubmit={handleAddColumn} className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-slate-900">Nova Coluna</h2>
              <button
                type="button"
                onClick={() => {
                  setShowColumnModal(false);
                  setNewColumnName('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <div>
              <label className="text-sm font-bold text-slate-700 block mb-2">Nome da Coluna *</label>
              <input
                type="text"
                required
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1b5e20] focus:border-transparent"
                placeholder="Ex: Em Revisão, Aguardando, etc."
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-2">
                A coluna será adicionada entre "A Fazer" e "Concluído"
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowColumnModal(false);
                  setNewColumnName('');
                }}
                className="flex-1 py-3 border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 py-3 bg-[#1b5e20] text-white rounded-xl font-bold hover:bg-[#15501a] transition-colors"
              >
                Criar Coluna
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}